import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { INITIATIVES, MY_INITIATIVES, CREATE_INITIATIVE, JOIN_INITIATIVE, LEAVE_INITIATIVE } from '../graphql';
import { Plus, X, Users, Sparkles, Handshake, BookOpen, Code, Drama, Leaf, HeartPulse, Lightbulb } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = ['ALL','SOCIAL','ACADEMIC','TECHNICAL','CULTURAL','ENVIRONMENT','HEALTH'];
const CAT_CONFIG = {
  SOCIAL:      { icon: Handshake, color:'bg-blue-100 text-blue-700' },
  ACADEMIC:    { icon: BookOpen,  color:'bg-brand-100 text-brand-700' },
  TECHNICAL:   { icon: Code,      color:'bg-brand-100 text-brand-700' },
  CULTURAL:    { icon: Drama,     color:'bg-orange-100 text-orange-700' },
  ENVIRONMENT: { icon: Leaf,      color:'bg-green-100 text-green-700' },
  HEALTH:      { icon: HeartPulse, color:'bg-red-100 text-red-700' },
};

function InitiativeCard({ initiative, currentUserId, onJoin, onLeave }) {
  const [showMembers, setShowMembers] = useState(false);
  const isMember = initiative.members?.some(m => m.user?.id === currentUserId);
  const isCreator = initiative.creator?.id === currentUserId;
  const cfg = CAT_CONFIG[initiative.category] || { icon: Lightbulb, color:'bg-gray-100 text-gray-600' };
  const full = initiative.maxMembers > 0 && initiative.memberCount >= initiative.maxMembers;

  return (
    <div className="card !p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <cfg.icon size={18} className="text-gray-400 flex-shrink-0" />
            <h3 className="font-bold text-gray-900">{initiative.title}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{initiative.category}</span>
            {!initiative.isOpen && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Closed</span>}
          </div>
          <p className="text-sm text-gray-500 mb-2">{initiative.description}</p>
          {initiative.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {initiative.tags.map(t => <span key={t} className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full">#{t}</span>)}
            </div>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <button onClick={() => setShowMembers(s=>!s)} className="flex items-center gap-1 hover:text-gray-600 transition-colors">
              <Users size={11}/> {initiative.memberCount} member{initiative.memberCount!==1?'s':''}
              {initiative.maxMembers > 0 && ` / ${initiative.maxMembers} max`}
            </button>
            {initiative.college && <span>{initiative.college}</span>}
            <span>by {initiative.creator?.name}</span>
          </div>
        </div>
        <div className="flex-shrink-0">
          {!isCreator && initiative.isOpen && !full && (
            isMember
              ? <button onClick={() => onLeave(initiative.id)} className="btn-secondary text-xs px-3 py-1.5 text-red-500">Leave</button>
              : <button onClick={() => onJoin(initiative.id)} className="btn-primary text-xs px-3 py-1.5">Join</button>
          )}
          {isMember && isCreator && <span className="text-xs text-brand-600 font-semibold">Creator</span>}
          {isMember && !isCreator && <span className="text-xs text-green-600 font-semibold">✓ Joined</span>}
          {full && !isMember && <span className="text-xs text-gray-400">Full</span>}
        </div>
      </div>

      {showMembers && initiative.members?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex flex-wrap gap-1">
            {initiative.members.map(m => (
              <div key={m.user?.id} className="flex items-center gap-1 bg-gray-50 rounded-full px-2 py-1 text-xs text-gray-600">
                <div className="w-4 h-4 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-xs">{m.user?.name?.[0]}</div>
                {m.user?.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function InitiativesPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('browse');
  const [category, setCategory] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title:'', description:'', category:'SOCIAL', tags:'', maxMembers:'', college:'' });

  const { data, refetch } = useQuery(INITIATIVES, { variables: { category: category || undefined } });
  const { data: myData, refetch: refetchMy } = useQuery(MY_INITIATIVES);
  const [create, { loading: creating }] = useMutation(CREATE_INITIATIVE, { onCompleted: () => { refetch(); refetchMy(); setShowCreate(false); }});
  const [join] = useMutation(JOIN_INITIATIVE, { onCompleted: () => { refetch(); refetchMy(); }});
  const [leave] = useMutation(LEAVE_INITIATIVE, { onCompleted: () => { refetch(); refetchMy(); }});

  const allInitiatives = data?.initiatives || [];
  const myInitiatives = myData?.myInitiatives || [];

  const handleCreate = async (e) => {
    e.preventDefault();
    await create({ variables: { ...form, tags: form.tags.split(',').map(s=>s.trim()).filter(Boolean), maxMembers: parseInt(form.maxMembers)||0, college: form.college||null } });
    setForm({ title:'',description:'',category:'SOCIAL',tags:'',maxMembers:'',college:'' });
  };

  const displayList = tab === 'mine' ? myInitiatives.map(i => allInitiatives.find(x=>x.id===i.id)||i) : allInitiatives;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-xl"><Sparkles size={20} className="text-green-600"/></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Initiatives</h1>
            <p className="text-gray-500 text-sm">Create a movement · Join a cause · Build something meaningful together</p>
          </div>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14}/> Create Initiative</button>
      </div>

      {/* Tabs & Category filter */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {[['browse','Browse'],['mine','Mine']].map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${tab===t?'bg-white shadow text-gray-900':'text-gray-500'}`}>{l}{t==='mine' && myInitiatives.length > 0 ? ` (${myInitiatives.length})` : ''}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => { const v=c==='ALL'?'':c; setCategory(v); refetch({ category: v||undefined }); }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${(c==='ALL'&&!category)||(category===c) ? 'bg-green-600 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-green-300'}`}>
              {c==='ALL'?'All':c}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[['Total', allInitiatives.length,'bg-green-50 text-green-700'],['Joined', myInitiatives.length,'bg-brand-50 text-brand-700'],['Members', allInitiatives.reduce((s,i)=>s+i.memberCount,0),'bg-orange-50 text-orange-700']].map(([l,v,c])=>(
          <div key={l} className={`${c} rounded-xl p-3 text-center`}><p className="text-xl font-bold">{v}</p><p className="text-xs font-medium mt-0.5">{l}</p></div>
        ))}
      </div>

      {/* List */}
      {displayList.length === 0 ? (
        <div className="text-center py-16">
          <Lightbulb size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 font-medium">{tab === 'mine' ? 'You haven\'t joined any initiative yet' : 'No initiatives yet'}</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">+ Create Initiative</button>
        </div>
      ) : (
        <div className="space-y-3">
          {displayList.map(i => (
            <InitiativeCard key={i.id} initiative={i} currentUserId={user?.id}
              onJoin={id => join({ variables: { initiativeId: id } })}
              onLeave={id => leave({ variables: { initiativeId: id } })} />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">Create Initiative</h2>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-gray-400"/></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              <input required className="input w-full" placeholder="Initiative title" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} />
              <textarea required className="input w-full resize-none h-20 text-sm" placeholder="What is this initiative about? What's the goal?" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Category</label>
                  <select className="input w-full" value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>
                    {CATEGORIES.filter(c=>c!=='ALL').map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Max Members (0=unlimited)</label>
                  <input type="number" min="0" className="input w-full" value={form.maxMembers} onChange={e=>setForm(f=>({...f,maxMembers:e.target.value}))} placeholder="0" />
                </div>
              </div>
              <input className="input w-full" placeholder="Tags (comma-sep: nss, sustainability, coding)" value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} />
              <input className="input w-full" placeholder="College (blank = open to all universities)" value={form.college} onChange={e=>setForm(f=>({...f,college:e.target.value}))} />
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">{creating ? 'Creating…' : 'Create Initiative'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
